// Dekning — SVG ring-gauge (klientkonto / utestående). Når klientkonto-kilden ikke er
// wiret (dekningPct = null) vises en "ikke tilgjengelig"-tilstand i stedet for tall.
import { Skeleton } from "@/components/ui/skeleton";
import { formatAmount } from "../api/format";
import type { ExposureResponse } from "../api/types";

interface CoverageRingProps {
  data: ExposureResponse | undefined;
  isLoading: boolean;
}

const R = 74;
const CIRC = 2 * Math.PI * R; // ≈ 464.96

export function CoverageRing({ data, isLoading }: CoverageRingProps) {
  return (
    <div className="glass px-6 py-[22px]">
      <h2 className="m-0 mb-4 flex justify-between text-[13px] font-semibold text-[var(--sub)]">
        Dekning
        <span className="font-medium text-[var(--faint)]">Oblinor</span>
      </h2>

      {isLoading ? (
        <div className="flex flex-col items-center">
          <Skeleton className="h-[180px] w-[180px] rounded-full bg-[rgba(120,60,10,0.1)]" />
        </div>
      ) : (
        <Ring data={data} />
      )}
    </div>
  );
}

function Ring({ data }: { data: ExposureResponse | undefined }) {
  const pct = data?.dekningPct ?? null;
  const available = pct != null;
  const clamped = available ? Math.max(0, Math.min(100, pct)) : 0;
  const dashoffset = available ? CIRC * (1 - clamped / 100) : CIRC;

  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="relative h-[180px] w-[180px]">
        <svg width="180" height="180" viewBox="0 0 180 180">
          <defs>
            <linearGradient id="si-ring" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#FFD9A3" />
              <stop offset="1" stopColor="#C35A1A" />
            </linearGradient>
          </defs>
          <circle cx="90" cy="90" r={R} fill="none" stroke="rgba(120,60,10,.14)" strokeWidth="14" />
          {available && (
            <circle
              cx="90"
              cy="90"
              r={R}
              fill="none"
              stroke="url(#si-ring)"
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={dashoffset}
              transform="rotate(-90 90 90)"
              style={{ filter: "drop-shadow(0 0 8px rgba(242,139,44,.6))" }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-[30px] font-bold text-[var(--val)]">
            {available ? `${Math.round(clamped)} %` : "—"}
          </div>
          <div className="mt-0.5 text-[11px] text-[var(--sub)]">klientkonto / utestående</div>
        </div>
      </div>
      <div className="mt-3.5 text-[11.5px] leading-[1.5] text-[var(--faint)]">
        {available ? (
          <>
            {formatAmount(data?.klientkonto)} av {formatAmount(data?.sumUtestaaende)} dekket
            {data?.underdekning ? (
              <>
                {" "}→ <span className="neg">underdekning {formatAmount(data.underdekning)}</span>
              </>
            ) : null}
          </>
        ) : (
          "Klientkonto-kilde ikke koblet på ennå."
        )}
      </div>
    </div>
  );
}
